<template>
  <div
    v-if="shouldShowBottomBar"
    class="platform-bottom-bar"
    :class="{
      'platform-bottom-bar--solid': shouldUseSolidVariant,
      'platform-bottom-bar--dynamic': shouldUseDynamicVariant,
    }"
    :style="bottomBarStyleVars"
    data-qa="platform-bottom-bar"
  >
    <VcBottomNav
      :tabs="translatedTabs"
      v-model="selectedTab"
      :isLoading="isLoading"
      @change="onTabChange"
      @click.native="onBottomNavClick"
    >
      <template #tab-1="data">
        <VcBadge :badgeText="data.item.badgeValue" :offset-y="12" :offset-x="8" size="sm">
          <PulseMenuIcon v-if="data.item.name === 'pulse'" class="pulse-menu-icon" />
          <VcIcon v-else color="currentColor" size="20">
            {{ data.item.icon }}
          </VcIcon>
        </VcBadge>
      </template>
    </VcBottomNav>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import LayoutConstants from '@/constansts/layoutConstants';
import outgoingPostMessageUtils from '@/utils/eventCommunicationUtils/outgoingPostMessageUtils';
import {
  MOBILE_PLATFORM_BOTTOM_ACTION_TYPES,
} from '@/modules/layout/platformNavigation/utils/mobilePlatformBottomTabs';
import { executeAction, hasAction } from '@/modules/layout/platformNavigation/utils/actionExecutor';
import MOBILE_PLATFORM_ROUTE_NAMES from '@/modules/layout/platformNavigation/utils/mobilePlatformRoutes';
import PulseMenuIcon from '@/assets/images/pulse_menu.svg';

const FULL_WIDTH_TAB_COUNT = 5;

export default {
  name: 'PlatformBottomBar',
  components: { PulseMenuIcon },
  computed: {
    ...mapGetters('LayoutBottomNavStore', ['bottomNavTabs', 'showBottomNav', 'currentIndex', 'isLoading']),
    ...mapGetters(['isInitialDataLoaded']),
    ...mapGetters('PlatformNavigationStore', ['activeTopBarItemUid', 'isActiveItemBMP']),
    shouldShowBottomBar() {
      return this.showBottomNav && !this.isMobilePlatformHomeRoute;
    },
    isMobilePlatformHomeRoute() {
      return this.$route.name === MOBILE_PLATFORM_ROUTE_NAMES.HOME;
    },
    shouldUseSolidVariant() {
      return this.$route.path.startsWith('/my-livesite');
    },
    shouldUseDynamicVariant() {
      const dynamicActionTypes = Object.values(MOBILE_PLATFORM_BOTTOM_ACTION_TYPES);
      return this.bottomNavTabs.some((tab) => dynamicActionTypes.includes(tab.actionType))
        || (this.isActiveItemBMP && this.bottomNavTabs.length < FULL_WIDTH_TAB_COUNT);
    },
    bottomBarStyleVars() {
      if (!this.shouldUseDynamicVariant) return {};

      return {
        '--platform-bottom-bar-tab-count': this.translatedTabs.length,
      };
    },
    translatedTabs() {
      return this.bottomNavTabs.map((tab) => ({
        ...tab,
        label: tab.isAlreadyTranslated ? tab.label : this.$t(tab.label),
      }));
    },
    currentTab() {
      return this.translatedTabs[this.selectedTab];
    },
    selectedTab: {
      get() {
        return this.currentIndex;
      },
      set(newIndex) {
        this.setCurrentIndex(newIndex);
      },
    },
  },
  created() {
    if (this.isInitialDataLoaded) {
      this.initBottomNav();
    }
  },
  methods: {
    ...mapMutations('LayoutBottomNavStore', ['setCurrentIndex']),
    ...mapActions('LayoutBottomNavStore', ['createBottomNavTabs', 'updateInboxTabConversationCounts', 'updatePulseTabNeedsAttentionCount']),
    ...mapActions('MainMenuStore', ['updateShouldShowMenu']),
    ...mapActions('PlatformNavigationStore', {
      platformHandleSidebarItemClick: 'handleSidebarItemClick',
      showMobileModuleMenu: 'showMobileModuleMenu',
    }),
    async initBottomNav() {
      await this.createBottomNavTabs();
      await Promise.all([
        this.updateInboxTabConversationCounts(),
        this.updatePulseTabNeedsAttentionCount(),
      ]);
    },
    onTabChange() {
      this.$track('nav_item_clicked', { source: 'mobile_bottom_bar', item_name: this.currentTab?.name });
      this.executeTabAction(this.currentTab);
    },
    onBottomNavClick() {
      if (this.translatedTabs.length !== 1 || this.currentIndex !== 0) return;

      const tab = this.translatedTabs[0];
      this.$track('nav_item_clicked', { source: 'mobile_bottom_bar', item_name: tab?.name });
      this.executeTabAction(tab);
    },
    executeTabAction(tab) {
      if (!tab) return;
      if (tab.actionType === MOBILE_PLATFORM_BOTTOM_ACTION_TYPES.PLATFORM_NAVIGATION) {
        this.executePlatformNavigationTab(tab);
        return;
      }
      if (tab.actionType === MOBILE_PLATFORM_BOTTOM_ACTION_TYPES.MODULE_MENU) {
        this.showMobileModuleMenu();
        this.updateShouldShowMenu(true);
        return;
      }
      if (tab.actionType === MOBILE_PLATFORM_BOTTOM_ACTION_TYPES.MODULE_GRID) {
        this.navigateToMobilePlatformHome();
        return;
      }

      if (tab.name !== 'menu') {
        this.updateShouldShowMenu(false);
      }

      const { BOTTOM_NAV_ACTION_TYPES, NAVIGATE_TO, FIRE_ACTION } = LayoutConstants;

      switch (tab.actionType) {
        case BOTTOM_NAV_ACTION_TYPES.ANGULAR_NAVIGATION:
          outgoingPostMessageUtils.post(NAVIGATE_TO, { route: tab.action });
          outgoingPostMessageUtils.post(FIRE_ACTION, { event_name: 'pov-menu-item-clicked' });
          break;
        case BOTTOM_NAV_ACTION_TYPES.POV_NAVIGATION:
          this.$router.push(tab.action).catch(() => {});
          break;
        case BOTTOM_NAV_ACTION_TYPES.POV_ACTION:
          this.$store.dispatch(tab.dispatchAction.action, { value: tab.dispatchAction.value });
          break;
        case BOTTOM_NAV_ACTION_TYPES.ANGULAR_ACTION:
          outgoingPostMessageUtils.post(FIRE_ACTION, tab.action);
          break;
        default:
          break;
      }
    },
    executePlatformNavigationTab(tab) {
      this.updateShouldShowMenu(false);
      const item = tab.platformNavigationItem;
      this.platformHandleSidebarItemClick({ item });
      if (hasAction(item)) {
        executeAction(item.action);
      }
    },
    navigateToMobilePlatformHome() {
      this.updateShouldShowMenu(false);
      if (this.$route.name === MOBILE_PLATFORM_ROUTE_NAMES.HOME) return;

      this.$router.push({ name: MOBILE_PLATFORM_ROUTE_NAMES.HOME });
    },
  },
  watch: {
    isInitialDataLoaded(newValue, oldValue) {
      if (newValue && !oldValue) {
        this.initBottomNav();
      }
    },
    activeTopBarItemUid(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.initBottomNav();
      }
    },
    isActiveItemBMP(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.initBottomNav();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.pulse-menu-icon {
  width: 20px;
  height: 20px;
}

.platform-bottom-bar {
  position: fixed;
  left: var(--size-value3);
  right: var(--size-value3);
  bottom: var(--floating-mobile-bottom-bar-bottom, calc(env(safe-area-inset-bottom, 0px) + var(--size-value3, 12px)));
  z-index: 10;

  ::v-deep {
    .VcBottomNav {
      overflow: hidden;
      border: 1px solid rgba(221, 228, 235, 0.50);
      border-radius: 12px;
      background:
        linear-gradient(180deg, rgba(255, 255, 255, 0.34), rgba(248, 250, 252, 0.08)),
        rgba(255, 255, 255, 0.06);
      box-shadow:
        0 6px 18px rgba(15, 23, 42, 0.20),
        0 2px 6px rgba(15, 23, 42, 0.08),
        inset 0 1px 0 rgba(255, 255, 255, 0.40);
      backdrop-filter: blur(24px) saturate(150%);
      -webkit-backdrop-filter: blur(24px) saturate(150%);
      height: 52px;
    }

    .v-slide-group__wrapper {
      overflow: visible;
    }

    .v-slide-group__content {
      margin: 0 1px;
    }

    .v-tabs-bar {
      height: 52px;
    }

    .v-tabs-slider-wrapper {
      display: none;
    }

    .v-tab::before,
    .v-tab:hover::before,
    .v-tab:focus::before,
    .v-tab--active::before,
    .v-tab--active:hover::before,
    .v-tab--active:focus::before {
      opacity: 0 !important;
      background-color: transparent !important;
    }

    .VcBottomNav--tab {
      height: 48px;
      margin: 1px 0;
      border-radius: 12px;
      transition: transform 180ms ease, background-color 180ms ease, box-shadow 180ms ease;
    }

    .VcBottomNav--item {
      margin: 0;
      margin-top: 3px;
      justify-content: space-between !important;
      height: 34px;

      i {
        font-size: 18px !important;
      }
    }

    .VcBottomNav--tab--active {
      pointer-events: auto;
      color: var(--gray-darken-5);
      background-color: rgb(var(--brand-color-secondary-rgb) / 0.2);
      box-shadow: none;
      transform: none;

      .v-icon,
      .VcBottomNav--label {
        color: var(--gray-darken-5) !important;
      }

      .VcBottomNav--label {
        font-weight: var(--font-weight-large);
      }
    }

    .VcBottomNav--label {
      display: block;
      box-sizing: border-box;
      margin-top: 0px !important;
      padding-inline: 2px;
      width: 100%;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 11px;
    }
  }
}

.platform-bottom-bar--dynamic {
  left: auto;
  width: min(
    calc(var(--platform-bottom-bar-tab-count) * var(--floating-mobile-bottom-tab-width, 64px)),
    calc(100% - var(--size-value6))
  );
}

.platform-bottom-bar--solid {
  ::v-deep {
    .VcBottomNav {
      border: 1px solid var(--neutral-lighten-1);
      background: #ffffff;
      box-shadow:
        0 6px 18px rgba(15, 23, 42, 0.12),
        0 2px 6px rgba(15, 23, 42, 0.06);
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
    }
  }
}
</style>
